import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthGuard } from './services/auth-guard';

import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule, MatInputModule, MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material';
import { MatRadioModule } from '@angular/material/radio';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './services/auth-interceptor';
import { HeaderComponent } from './components/header/header.component';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { FooterComponent } from './components/footer/footer.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BasketsProductComponent } from './components/landing-page/baskets-product/baskets.component';
import { FreshProduceComponent } from './components/landing-page/fresh-produce/fresh-produce.component';
import { SaladDressingsComponent } from './components/landing-page/salad-dressings/salad-dressings.component';
import { BasketComponent } from './components/landing-page/baskets-product/basket/basket.component';
import { ProduceComponent } from './components/landing-page/fresh-produce/produce/produce.component';
import { DressingComponent } from './components/landing-page/salad-dressings/dressing/dressing.component';
import { CheckoutComponent } from './components/checkout/checkout.component';
import { FaqComponent } from './components/checkout/faqs/faq/faq.component';
import { FaqsComponent } from './components/checkout/faqs/faqs.component';
import { ProfileSettingsComponent } from './components/profile-settings/profile-settings.component';
import { ProfileComponent } from './components/profile-settings/profile/profile.component';
import { PastOrdersComponent } from './components/profile-settings/past-orders/past-orders.component';
import { EditProfileComponent } from './components/profile-settings/profile/edit-profile/edit-profile.component';
import { AddAddressComponent } from './components/profile-settings/add-address/add-address.component';
import { AddCreditCardComponent } from './components/profile-settings/add-credit-card/add-credit-card.component';
import { CartComponent } from './components/cart/cart.component';
import { LoginComponent } from './components/auth/login/login.component';
import { SignupComponent } from './components/auth/signup/signup.component';
import { AdminLoginComponent } from './components/auth/admin-login/admin-login.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LeftPanelComponent } from './components/dashboard/left-panel/left-panel.component';
import { RightPanelComponent } from './components/dashboard/right-panel/right-panel.component';
import { ProductsComponent } from './components/dashboard/right-panel/products/products.component';
import { ProductsTableComponent } from './components/dashboard/right-panel/products/products-table/products-table.component';
import { CreateProductComponent } from './components/dashboard/right-panel/products/create-product/create-product.component';
import { EditProductComponent } from './components/dashboard/right-panel/products/edit-product/edit-product.component';
import { BasketsComponent } from './components/dashboard/right-panel/baskets/baskets.component';
import { BasketsTableComponent } from './components/dashboard/right-panel/baskets/baskets-table/baskets-table.component';
import { CreateBasketComponent } from './components/dashboard/right-panel/baskets/create-basket/create-basket.component';
import { EditBasketComponent } from './components/dashboard/right-panel/baskets/edit-basket/edit-basket.component';
import { VerifyMobileComponent } from './components/auth/verify-mobile/verify-mobile.component';
import { ResendVerificationComponent } from './components/auth/resend-verification/resend-verification.component';
import { ViewOrdersComponent } from './components/checkout/view-orders/view-orders.component';
import { DialogDeleteProductComponent } from './components/dialogs/dialog-delete-product/dialog-delete-product.component';
import { DialogDeleteBasketComponent } from './components/dialogs/dialog-delete-basket/dialog-delete-basket.component';
import { EditAddressComponent } from './components/profile-settings/edit-address/edit-address.component';
import { OrdersComponent } from './components/dashboard/right-panel/orders/orders.component';
import { DeliveriesComponent } from './components/dashboard/right-panel/deliveries/deliveries.component';
import { DeliveriesTableComponent } from './components/dashboard/right-panel/deliveries/deliveries-table/deliveries-table.component';
import { OrdersTableComponent } from './components/dashboard/right-panel/orders/orders-table/orders-table.component';
import { DialogDeliveryStatusComponent } from './components/dialogs/dialog-delivery-status/dialog-delivery-status.component';
import { DialogDeliveryDateComponent } from './components/dialogs/dialog-delivery-date/dialog-delivery-date.component';
import { UsersComponent } from './components/dashboard/right-panel/users/users.component';
import { UsersTableComponent } from './components/dashboard/right-panel/users/users-table/users-table.component';
import { CreateUserComponent } from './components/dashboard/right-panel/users/create-user/create-user.component';
import { EditUserComponent } from './components/dashboard/right-panel/users/edit-user/edit-user.component';
import { UserOrdersComponent } from './components/dashboard/right-panel/users/user-orders/user-orders.component';
import { CreateOrderComponent } from './components/dashboard/right-panel/users/create-order/create-order.component';
import { DialogAddOrderPaymentComponent } from './components/dialogs/dialog-add-order-payment/dialog-add-order-payment.component';
import { DialogVideoComponent } from './components/dialogs/dialog-video/dialog-video.component';
import { DialogEditDeliveryDatesComponent } from './components/dialogs/dialog-edit-delivery-dates/dialog-edit-delivery-dates.component';
import { EditOrderComponent } from './components/dashboard/right-panel/users/edit-order/edit-order.component';
import { DialogUpdateSingleDeliveryDateComponent } from './components/dialogs/dialog-update-single-delivery-date/dialog-update-single-delivery-date.component';
import { ResetPasswordEnterMobileComponent } from './components/auth/reset-password-enter-mobile/reset-password-enter-mobile.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { FaqPageComponent } from './components/faq-page/faq-page.component';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LandingPageComponent,
    FooterComponent,
    BasketsProductComponent,
    FreshProduceComponent,
    SaladDressingsComponent,
    BasketComponent,
    ProduceComponent,
    DressingComponent,
    CheckoutComponent,
    FaqComponent,
    FaqsComponent,
    ProfileSettingsComponent,
    ProfileComponent,
    PastOrdersComponent,
    EditProfileComponent,
    AddAddressComponent,
    AddCreditCardComponent,
    CartComponent,
    LoginComponent,
    SignupComponent,
    AdminLoginComponent,
    DashboardComponent,
    LeftPanelComponent,
    RightPanelComponent,
    ProductsComponent,
    ProductsTableComponent,
    CreateProductComponent,
    EditProductComponent,
    BasketsComponent,
    BasketsTableComponent,
    CreateBasketComponent,
    EditBasketComponent,
    VerifyMobileComponent,
    ResendVerificationComponent,
    ViewOrdersComponent,
    DialogDeleteProductComponent,
    DialogDeleteBasketComponent,
    EditAddressComponent,
    OrdersComponent,
    DeliveriesComponent,
    DeliveriesTableComponent,
    OrdersTableComponent,
    DialogDeliveryStatusComponent,
    DialogDeliveryDateComponent,
    UsersComponent,
    UsersTableComponent,
    CreateUserComponent,
    EditUserComponent,
    UserOrdersComponent,
    CreateOrderComponent,
    DialogAddOrderPaymentComponent,
    DialogVideoComponent,
    DialogEditDeliveryDatesComponent,
    EditOrderComponent,
    DialogUpdateSingleDeliveryDateComponent,
    ResetPasswordEnterMobileComponent,
    ResetPasswordComponent,
    PrivacyPolicyComponent,
    TermsAndConditionsComponent,
    FaqPageComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatButtonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatMenuModule,
    MatDialogModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCardModule,
    MatSnackBarModule,
    MatSelectModule,
    MatStepperModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatCheckboxModule
  ],
  entryComponents: [
    EditProfileComponent,
    AddAddressComponent,
    AddCreditCardComponent,
    LoginComponent,
    SignupComponent,
    ResetPasswordComponent,
    ResetPasswordEnterMobileComponent,
    VerifyMobileComponent,
    ResendVerificationComponent,
    ViewOrdersComponent,
    EditAddressComponent,
    DialogDeleteProductComponent,
    DialogDeleteBasketComponent,
    DialogDeliveryDateComponent,
    DialogDeliveryStatusComponent,
    DialogAddOrderPaymentComponent,
    DialogVideoComponent,
    DialogEditDeliveryDatesComponent,
    DialogUpdateSingleDeliveryDateComponent
  ],
  providers: [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }, AuthGuard, { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
